import { useState } from 'react';
import { formatEuro, withVat, type SignatureSummary } from './signatureSummary';

interface Props {
  summary: SignatureSummary;
}

const card = 'bg-white rounded-2xl border border-black/[0.08] shadow-sm overflow-hidden';
const label = 'text-[11px] font-semibold uppercase tracking-wide text-[#6B6762]';

/**
 * Riepilogo economico della pagina di firma: una tantum, canoni e rate,
 * IVA esclusa come nel contratto, con interruttore per vedere i prezzi IVA inclusa.
 */
export function SignaturePriceSummary({ summary: s }: Props) {
  const [vatIncluded, setVatIncluded] = useState(false);
  const v = (n: number) => formatEuro(vatIncluded ? withVat(n, s.vatRate) : n);
  const hasRecurring = s.monthlyTotal > 0 || s.annualTotal > 0;
  const monthsLabel = (() => {
    const m = [...new Set(s.monthly.map((l) => l.months).filter(Boolean))];
    return m.length === 1 ? `al mese, per ${m[0]} mesi` : 'al mese';
  })();

  return (
    <>
      <section className={card}>
        <div className="px-6 sm:px-8 py-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className={label}>Quanto costa</div>
            <div className="inline-flex rounded-lg bg-[#F7F6F3] p-0.5" role="group" aria-label="Mostra i prezzi">
              {[false, true].map((inc) => (
                <button
                  key={String(inc)}
                  type="button"
                  aria-pressed={vatIncluded === inc}
                  onClick={() => setVatIncluded(inc)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                    vatIncluded === inc ? 'bg-white text-[#37352F] font-semibold shadow-sm' : 'text-[#6B6762]'
                  }`}
                >
                  {inc ? 'IVA inclusa' : 'IVA esclusa'}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {s.oneTimeTotal > 0 && (
              <PriceRow title={hasRecurring ? 'Progetto' : 'Totale'} sub="una tantum" value={v(s.oneTimeTotal)} big />
            )}
            {s.monthlyTotal > 0 && <PriceRow title={s.monthly.length === 1 ? s.monthly[0].title : 'Servizi mensili'} sub={monthsLabel} value={v(s.monthlyTotal)} big={s.oneTimeTotal === 0} />}
            {s.annualTotal > 0 && <PriceRow title={s.annual.length === 1 ? s.annual[0].title : 'Servizi annuali'} sub="all'anno" value={v(s.annualTotal)} />}
            {hasRecurring && s.oneTimeTotal > 0 && (
              <div className="pt-3 border-t border-dashed border-black/[0.10] flex justify-between text-sm text-[#6B6762]">
                <span>Totale primo anno</span>
                <span className="tabular-nums">{v(s.firstYearTotal)}</span>
              </div>
            )}
          </div>
          <div className="mt-2 text-xs text-[#9D9892]">
            {vatIncluded ? `Prezzi con IVA ${s.vatRate}% inclusa` : 'Prezzi IVA esclusa, come nel contratto'}
          </div>
        </div>
      </section>

      {s.installments.length > 0 && (
        <section className={card}>
          <div className="px-6 sm:px-8 py-5">
            <div className={label}>{hasRecurring ? 'Come si paga il progetto' : 'Come si paga'}</div>
            <ol className="mt-3">
              {s.installments.map((i, idx) => (
                <li key={i.label} className="relative grid grid-cols-[12px_1fr_auto] gap-3 py-2">
                  <span className="mt-1.5 h-3 w-3 rounded-full border-2 border-[#F57C2A] bg-white z-[1]" />
                  {idx < s.installments.length - 1 && (
                    <span className="absolute left-[5px] top-[22px] -bottom-2 w-0.5 bg-black/[0.08]" aria-hidden />
                  )}
                  <div>
                    <div className="font-medium text-[#37352F]">{i.label}</div>
                    <div className="text-xs text-[#9D9892]">{String(i.percent).replace('.', ',')}%</div>
                  </div>
                  <div className="font-semibold text-[#37352F] tabular-nums">{v(i.amount)}</div>
                </li>
              ))}
            </ol>
            {hasRecurring && (
              <p className="mt-1 text-xs text-[#9D9892]">I canoni si pagano a parte, alle loro scadenze.</p>
            )}
          </div>
        </section>
      )}
    </>
  );
}

function PriceRow({ title, sub, value, big }: { title: string; sub: string; value: string; big?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <div className="min-w-0">
        <div className="font-medium text-[#37352F]">{title}</div>
        <div className="text-sm text-[#6B6762]">{sub}</div>
      </div>
      <div className={`font-bold text-[#37352F] tabular-nums whitespace-nowrap ${big ? 'text-2xl sm:text-3xl' : 'text-xl'}`}>
        {value}
      </div>
    </div>
  );
}
